import jwt_decode, { JwtPayload } from "jwt-decode";
import { useEffect, useState } from "react";
import ReactGA4 from "react-ga4";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import AppRoutes from "./Routes";
import { APIStatus } from "./api/APIStatus.type";
import { Formations } from "./api/privateRequests/squad-builder/get-formations";
import PopupMessage from "./components/atoms/PopupMessage/PopupMessage";
import Footer from "./components/molecules/Footer/Footer";
import { ModalController } from "./components/molecules/Modals/ModalController";
import { WelcomeModal } from "./components/molecules/Modals/SpecificModals/WelcomeModal";
import {
  evolutionsSelector,
  fetchEvolutions,
} from "./redux/evolutions/evolutionsSlice";
import {
  fetchVersionAssets,
  getFormations,
  getManagerData,
  loadedContentSelector,
} from "./redux/loadedContent/loadedContentSlice";
import { getMessageSelector } from "./redux/message/messageSlice";
import { fetchNews, newsSelector } from "./redux/news/newsSlice";
import {
  getInitialPlayerData,
  playersSelector,
} from "./redux/players/playersSlice";
import { initSbcParams } from "./redux/sbcs/sbcFiltersSlice";
import { fetchSbcsSets, getSbcSetsSelector } from "./redux/sbcs/sbcSetsSlice";
import { loadFormations } from "./redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { AppDispatch } from "./redux/store";
import {
  fetchPrices,
  fetchUser,
  getUserSelector,
  userPricesSelector,
} from "./redux/user/userSlice";

function App() {
  const { user } = useSelector(getUserSelector);
  const { versionsStatus, leaguesCountriesClubsStatus, formationsStatus } =
    useSelector(loadedContentSelector);
  const { status: newsStatus } = useSelector(newsSelector);
  const { initialPlayerDataStatus } = useSelector(playersSelector);
  const { status: evolutionsStatus } = useSelector(evolutionsSelector);
  const { sbcsSets } = useSelector(getSbcSetsSelector);
  const [shouldIdentifyUserForHotjar, setShouldIdentifyUserForHotjar] =
    useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const { message, modal } = useSelector(getMessageSelector);
  const [versionStatusFailed, setVersionStatusFailed] = useState(0);
  const [initialPlayerDataFailed, setInitialPlayerDataFailed] = useState(0);
  const [leaguesCountriesClubsFailed, setLeaguesCountriesClubsFailed] =
    useState(0);
  const [newsFailed, setNewsFailed] = useState(0);
  const [formationsFailed, setFormationsFailed] = useState(0);
  const [evolutionsFailed, setEvolutionsFailed] = useState(0);
  const [sbcsSetsFailed, setSbcsSetsFailed] = useState(0);
  const prices = useSelector(userPricesSelector);

  useEffect(() => {
    dispatch(initSbcParams());
  }, [user.paid, dispatch]);

  useEffect(() => {
    if (!prices.length) dispatch(fetchPrices());
  }, [prices.length]);

  useEffect(() => {
    const hotjarIdentifyUser = async () => {
      if (window.hj && shouldIdentifyUserForHotjar) {
        let country: string | undefined;
        try {
          const response = await fetch("https://ipapi.co/json/");
          const data = await response.json();
          country = data.country_code;
        } catch (error) {
          console.error("Error fetching user country:", error);
        }

        window.hj("identify", user.uuid || undefined, {
          country: country || undefined,
          subscription:
            user?.subscription === "diamond"
              ? "ultimate"
              : user?.subscription === "gold"
                ? "elite"
                : user?.subscription === "silver"
                  ? "pro"
                  : "free",
          loggedIn: user?.uuid ? "true" : "false",
        });
        setShouldIdentifyUserForHotjar(false);
      }
    };
    const timeoutId = setTimeout(hotjarIdentifyUser, 10000);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (localStorage.getItem("access_token")) {
      try {
        jwt_decode<JwtPayload>(localStorage.getItem("access_token"));
        dispatch(fetchUser({ didLogin: false }));
      } catch {
        localStorage.removeItem("access_token");
      }
    }
    (window as any).rewardful("ready", function () {});
  }, []);

  // Max 3 retries
  const fetchInitialData = async ({
    status,
    failed,
    incrementFailed,
    functionToCall,
  }: {
    status: APIStatus;
    failed: number;
    incrementFailed: (failed: number) => void;
    functionToCall: () => void;
  }) => {
    if (status === "idle") {
      functionToCall();
    } else if (status === "rejected") {
      setTimeout(() => {
        if (failed < 2) {
          functionToCall();
          incrementFailed(failed + 1);
        }
      }, 1000);
    }
  };

  useEffect(() => {
    fetchInitialData({
      status: versionsStatus,
      failed: versionStatusFailed,
      incrementFailed: setVersionStatusFailed,
      functionToCall: () => dispatch(fetchVersionAssets()),
    });
  }, [dispatch, versionsStatus, versionStatusFailed]);

  // Initial player data
  useEffect(() => {
    fetchInitialData({
      status: initialPlayerDataStatus,
      failed: initialPlayerDataFailed,
      incrementFailed: setInitialPlayerDataFailed,
      functionToCall: () => dispatch(getInitialPlayerData()),
    });
  }, [initialPlayerDataStatus, dispatch, initialPlayerDataFailed]);

  // Evolutions
  useEffect(() => {
    fetchInitialData({
      status: evolutionsStatus,
      failed: evolutionsFailed,
      incrementFailed: setEvolutionsFailed,
      functionToCall: () => dispatch(fetchEvolutions()),
    });
  }, [evolutionsStatus, dispatch, evolutionsFailed]);

  // SBC sets
  useEffect(() => {
    fetchInitialData({
      status: sbcsSets.status,
      failed: sbcsSetsFailed,
      incrementFailed: setSbcsSetsFailed,
      functionToCall: () => dispatch(fetchSbcsSets()),
    });
  }, [sbcsSets.status, dispatch, sbcsSetsFailed]);

  // Manager data
  useEffect(() => {
    fetchInitialData({
      status: leaguesCountriesClubsStatus,
      failed: leaguesCountriesClubsFailed,
      incrementFailed: setLeaguesCountriesClubsFailed,
      functionToCall: () => dispatch(getManagerData()),
    });
  }, [leaguesCountriesClubsStatus, dispatch, leaguesCountriesClubsFailed]);

  // News
  useEffect(() => {
    fetchInitialData({
      status: newsStatus,
      failed: newsFailed,
      incrementFailed: setNewsFailed,
      functionToCall: () => dispatch(fetchNews()),
    });
  }, [newsStatus, dispatch, newsFailed]);

  // Formations
  useEffect(() => {
    fetchInitialData({
      status: formationsStatus,
      failed: formationsFailed,
      incrementFailed: setFormationsFailed,
      functionToCall: () =>
        dispatch(getFormations()).then((result) => {
          if (result.payload) {
            dispatch(loadFormations(result.payload as Formations));
          }
        }),
    });
  }, [formationsStatus, dispatch, formationsFailed]);

  console.log("modal: ", modal);
  return (
    <div id="body">
      <BrowserRouter>
        <div className="flex flex-col flex-start ">
          {message && (
            <PopupMessage
              title={message.title}
              content={message.content}
              fixed={message.fixed}
            />
          )}
          {modal &&
            (modal.modalType === "welcome-back" ||
            modal.modalType === "welcome" ? (
              <WelcomeModal welcomeBack={modal.modalType === "welcome-back"} />
            ) : (
              <ModalController
                modalType={modal?.modalType}
                premiumFeatureHook={modal?.premiumFeatureHook}
                freeFeatureLoggedInRequiredHook={
                  modal?.freeFeatureLoggedInRequiredHook
                }
                data={modal?.data}
              />
            ))}
          <AppRoutes user={user} />
          <Footer />
        </div>
      </BrowserRouter>
    </div>
  );
}

export default App;
